/**
 * PointsFormula Component
 * Displays the points calculation formula for points-based challenges
 */

import React, { memo, useMemo } from 'react'
import { List } from '@material-ui/core'
import { PointsLogic } from '../../ChallengeAdmin/components/ChallengePoints/ChallengePoints'
import { CustomTypeCategory } from '../../../services/CustomTypes'
import { formatThousands } from '../../../common/helpers/translations'
import { unitsCountPointsShortFunc } from '../../../common/helpers/units'
import { YumActivityIcon } from '../../../components/YumActivityIcon/YumActivityIcon'
import ChallengeInfoListItem from './ChallengeInfoListItem'
import {
  getActivityLabel,
  getActivityUnitText,
  calculateDailyMaxPoints,
  calculateTotalMaxPoints,
  shouldShowDailyMax,
  shouldShowTotalMax,
  formatPointsContent,
} from '../ChallengeInfo.helpers'

interface PointsFormulaProps {
  pointsLogic: PointsLogic[] | null
  roundDailyPoints: boolean
  isReview?: boolean
  style?: React.CSSProperties
}

/**
 * Renders a single points activity row
 */
const renderPointsActivity = (activity: PointsLogic, roundDailyPoints: boolean, isDayView?: boolean) => {
  const value = formatThousands(isDayView ? activity.dayValue : activity.value)
  const unit = getActivityUnitText(activity)

  // Get points - from dayData if day view, otherwise from activity
  let points = isDayView ? activity.dayPoints : activity.points
  let dailyMaxExceeded = false
  let totalMaxExceeded = false

  if (isDayView && activity.dayData) {
    points = activity.dayData.points
    dailyMaxExceeded = activity.dayData.dailyMaxExceeded === true
    totalMaxExceeded = activity.dayData.totalMaxExceeded === true
  }

  const pointsAmount = formatThousands(points)
  const pointsUnit = unitsCountPointsShortFunc(points)

  // Calculate daily max
  const dailyMax = calculateDailyMaxPoints(activity, roundDailyPoints)
  const maxPointsUnit = unitsCountPointsShortFunc(dailyMax)
  const showDailyMax = shouldShowDailyMax(activity, isDayView, dailyMaxExceeded)

  // Calculate total max
  const totalMax = calculateTotalMaxPoints(activity)
  const totalMaxUnit = unitsCountPointsShortFunc(totalMax)
  const showTotalMax = shouldShowTotalMax(totalMax, isDayView, totalMaxExceeded)

  return {
    label: getActivityLabel(activity),
    content: formatPointsContent(
      value,
      unit,
      pointsAmount,
      pointsUnit,
      showDailyMax,
      dailyMax,
      maxPointsUnit,
      dailyMaxExceeded,
      showTotalMax,
      totalMax,
      totalMaxUnit,
      totalMaxExceeded,
    ),
    Icon: (
      <YumActivityIcon
        category={activity.icon || activity.category}
        color={'darkBlue'}
        isInactive={true}
        extraPadding={true}
      />
    ),
  }
}

/**
 * PointsFormula displays the points calculation rules for a challenge
 * Shows how each activity type converts to points
 */
const PointsFormula: React.FC<PointsFormulaProps> = memo(({ pointsLogic, roundDailyPoints, isReview = false }) => {
  // Early return if no points logic
  if (!pointsLogic || pointsLogic.length === 0) {
    return null
  }

  // Memoize the filtered and transformed points data
  const pointsData = useMemo(() => {
    // Remove unknown categories
    const filteredLogic = pointsLogic.filter((activity) =>
      Object.values(CustomTypeCategory).includes(activity.category),
    )
    return filteredLogic.map((activity) => renderPointsActivity(activity, roundDailyPoints))
  }, [pointsLogic, roundDailyPoints])

  // Don't render if no valid data
  if (pointsData.length === 0) {
    return null
  }

  const listStyle = { marginTop: isReview ? 0 : 10 }

  return (
    <List style={listStyle}>
      {pointsData.map((data, index) => (
        <ChallengeInfoListItem key={index} data={data} index={index} isDense={true} />
      ))}
    </List>
  )
})

PointsFormula.displayName = 'PointsFormula'

export default PointsFormula
